import type { AllowlistRole, RoleFilter, User } from './types';

// The ONE people-scope predicate, shared by all three Account tabs — the People
// rows, the Heatmap rows, and the Activity feed scope — so "search + role
// filter" means exactly the same thing on every tab (the shared toolbar's whole
// point). The role chip narrows to one role; the free-text query matches an
// email substring OR a role-NAME substring (so typing "admin" finds the admins,
// not just emails that happen to contain the word). The two combine with AND.
//
// `role` is `string | null` so an off-list Heatmap row (activity but no longer
// on the allowlist) flows through the same rule: a null role can never match a
// role-name query and is always excluded by a specific role chip — only its
// email can match, and only when the chip is "all".
export function personMatches(p: { email: string; role: string | null }, q: string, role: RoleFilter): boolean {
  if (role !== 'all' && p.role !== role) return false;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return p.email.toLowerCase().includes(needle) || (p.role ?? '').toLowerCase().includes(needle);
}

// Client-side search + role filter for the account list (AC3). The allowlist
// endpoint returns every user in one shot (it is a small KV set), so the page
// holds the full list and filters / paginates in memory — no server round-trip
// per keystroke. Delegates to personMatches so the People table can't drift from
// the other tabs.
export function filterUsers(users: User[], q: string, role: RoleFilter): User[] {
  return users.filter((u) => personMatches(u, q, role));
}

// Chip counts over the FULL list (not the filtered view) so each chip shows how
// many it would match — same contract as the Dogs filter chips.
export function roleCounts(users: User[]): Record<'all' | AllowlistRole, number> {
  // `customer` has a bucket. Without one, the first registered customer made
  // `counts[u.role]++` read `undefined++`, which is NaN, silently.
  const counts: Record<'all' | AllowlistRole, number> = {
    all: users.length, admin: 0, owner: 0, helper: 0, staff: 0, customer: 0,
  };
  for (const u of users) counts[u.role]++;
  return counts;
}
